import React, { useState } from 'react';
import { SignIn } from './SignIn';
import { SignUp } from './SignUp';
import { Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

interface AuthTabsProps {
  initialTab?: 'signin' | 'signup';
  onSwitchToSignIn?: () => void;
  onSwitchToSignUp?: () => void;
}

export function AuthTabs({ initialTab = 'signin', onSwitchToSignIn, onSwitchToSignUp }: AuthTabsProps) {
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>(initialTab);

  const handleTabChange = (tab: 'signin' | 'signup') => {
    if (tab === 'signin' && activeTab === 'signup' && onSwitchToSignIn) {
      onSwitchToSignIn();
    } else if (tab === 'signup' && activeTab === 'signin' && onSwitchToSignUp) {
      onSwitchToSignUp();
    } else {
      setActiveTab(tab);
    }
  };

  return (
    <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
      {/* Logo Section - Fixed at top */}
      <div className="sticky top-0 bg-bw-surface pt-6 px-6 pb-4 border-b border-bw-border">
        <div className="text-center space-y-4">
          <div className="flex justify-center items-center">
            <div className="flex items-center justify-center">
              <Zap className="h-10 w-10 text-bw-accent" />
              <span className="ml-2 text-3xl font-bold text-bw-primary">BidWin</span>
            </div>
          </div>
          <div className="text-sm text-bw-muted">by</div>
          <div className="flex items-center justify-center">
            <span className="text-xl font-semibold text-bw-accent">SerVet Digital</span>
          </div>
        </div>
      </div>

      {/* Tabs - Fixed below logo */}
      <div className="sticky top-[156px] bg-bw-surface z-10">
        <div className="flex border-b border-bw-border">
          <button
            className={`flex-1 py-3 px-4 text-center ${
              activeTab === 'signin'
                ? 'border-b-2 border-servet-red text-bw-accent'
                : 'text-bw-muted hover:text-bw-ink'
            }`}
            onClick={() => handleTabChange('signin')}
          >
            Sign In
          </button>
          <button
            className={`flex-1 py-3 px-4 text-center ${
              activeTab === 'signup'
                ? 'border-b-2 border-servet-red text-bw-accent'
                : 'text-bw-muted hover:text-bw-ink'
            }`}
            onClick={() => handleTabChange('signup')}
          >
            Sign Up
          </button>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="px-6 pt-4 pb-6">
        {/* Auth Form */}
        <div className="mb-6">
          {activeTab === 'signin' ? (
            <SignIn onSwitchToSignUp={() => handleTabChange('signup')} />
          ) : (
            <SignUp onSwitchToSignIn={() => handleTabChange('signin')} />
          )}
        </div>

        {/* Terms and Privacy - Sticky at bottom */}
        <div className="text-center text-sm text-bw-muted mt-4">
          <p>
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-bw-primary hover:text-bw-accent">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-bw-primary hover:text-bw-accent">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}